import apiService from './api';
import { errorMessage, unwrap } from '../utils/apiResponse';
import type {
  PestModel,
  PestModelInput,
  MatchedFarm,
  PestRiskSeries,
  PestOverviewItem,
  PestBiofix,
  PestAlertRule,
  PestAlertRuleInput,
  PestAlertEvent,
  PestLookupResult,
  PestIdentity,
  PestModelResearch,
  PestGuideResearch,
  PestKind,
  PestModelType,
  PestChemicalsResult,
  PestTargetLink,
  RegisteredPestOption,
  FarmProtection,
} from '../types/pest';
import { defaultParams } from '../components/spray/pestModelDefaults';

export type LookupStep = 'identify' | 'model' | 'guide';
export type LookupStepState = 'running' | 'done' | 'failed';
export interface LookupProgress { step: LookupStep; state: LookupStepState; detail?: string }

const DEFAULT_TYPE_FOR_KIND: Record<PestKind, PestModelType> = {
  disease: 'generic_infection',
  insect: 'degree_day',
  weed: 'risk_index',
};

/**
 * Pest & disease service — wraps /api/pest-disease.
 *
 * Dashboard reads (risk series, overview) return null on failure so pages stay
 * terse (chillUnits.ts convention). Admin/CRUD calls unwrap and throw so forms
 * can surface the error.
 */

interface RiskOpts {
  pestModelId: number;
  season?: number;
  refresh?: boolean;
}

class PestModelService {
  // ── models ────────────────────────────────────────────────────────────────
  async listModels(activeOnly = false): Promise<PestModel[]> {
    const res = await apiService.get<PestModel[]>(`/pest-disease/models${activeOnly ? '?active=true' : ''}`);
    return unwrap(res);
  }

  async getModel(id: number): Promise<PestModel> {
    const res = await apiService.get<PestModel>(`/pest-disease/models/${id}`);
    return unwrap(res);
  }

  async createModel(data: PestModelInput): Promise<PestModel> {
    const res = await apiService.post<PestModel>('/pest-disease/models', data);
    return unwrap(res);
  }

  async updateModel(id: number, data: Partial<PestModelInput>): Promise<PestModel> {
    const res = await apiService.put<PestModel>(`/pest-disease/models/${id}`, data);
    return unwrap(res);
  }

  async deleteModel(id: number): Promise<void> {
    unwrap(await apiService.delete(`/pest-disease/models/${id}`));
  }

  /**
   * AI research for a pest from whatever the grower knows about it. Returns a
   * proposal; nothing is saved.
   *
   * Three requests, not one: identify (quick), then the forecast model and the
   * field guide in parallel. The backend runs on Cloud Run, which ends any
   * request at 300 s, and a single call doing all of it ran past that. Split,
   * each step fits with room to spare, the page can say what is happening, and
   * a step that fails costs only its own part — the rest still arrives.
   */
  async lookup(
    query: string,
    cropHint?: string | null,
    onProgress?: (p: LookupProgress) => void,
  ): Promise<PestLookupResult> {
    const report = (step: LookupStep, state: LookupStepState, detail?: string) => onProgress?.({ step, state, detail });

    report('identify', 'running');
    let ident: PestIdentity | Extract<PestLookupResult, { identified: false }>;
    try {
      ident = unwrap(await apiService.post<typeof ident>(
        '/pest-disease/lookup/identify',
        { query, crop_hint: cropHint || null },
        undefined,
        { timeout: 120_000 },
      ));
    } catch (e) {
      report('identify', 'failed', errorMessage(e));
      throw e;
    }
    if (!ident.identified) { report('identify', 'failed', ident.message); return ident; }
    report('identify', 'done', ident.scientific_name ? `${ident.name} (${ident.scientific_name})` : ident.name);

    const subject = { name: ident.name, scientific_name: ident.scientific_name, kind: ident.kind, crop_hint: cropHint || null };
    const step = async <T,>(name: LookupStep, path: string): Promise<T> => {
      report(name, 'running');
      try {
        const r = unwrap(await apiService.post<T>(path, subject, undefined, { timeout: 290_000 }));
        report(name, 'done');
        return r;
      } catch (e) {
        report(name, 'failed', errorMessage(e));
        throw e;
      }
    };
    const [model, guide] = await Promise.allSettled([
      step<PestModelResearch>('model', '/pest-disease/lookup/model'),
      step<PestGuideResearch>('guide', '/pest-disease/lookup/guide'),
    ]);

    const warnings = [...ident.warnings];
    let modelPart: Pick<PestModelResearch, 'model_type' | 'model_params' | 'rationale' | 'recommended_actions'>;
    if (model.status === 'fulfilled') {
      modelPart = model.value;
      warnings.push(...model.value.warnings);
    } else {
      // Something the engine can run, flagged loudly: a pest saved on these
      // defaults would otherwise alert on numbers nobody looked up.
      const type = DEFAULT_TYPE_FOR_KIND[ident.kind];
      modelPart = { model_type: type, model_params: defaultParams(type), rationale: null, recommended_actions: null };
      warnings.push(`The forecast model could not be researched (${errorMessage(model.reason)}). Generic defaults are filled in — set the parameters from a source before relying on its alerts.`);
    }
    let guidePart: Pick<PestGuideResearch, 'description' | 'profile'> = { description: null, profile: {} };
    if (guide.status === 'fulfilled') {
      guidePart = guide.value;
      warnings.push(...guide.value.warnings);
    } else {
      warnings.push(`The field guide could not be researched (${errorMessage(guide.reason)}). Save the pest and use Research with AI to try again later.`);
    }

    return {
      ...ident,
      description: guidePart.description,
      recommended_actions: modelPart.recommended_actions,
      model_type: modelPart.model_type,
      model_params: modelPart.model_params,
      profile: { ...guidePart.profile, model_rationale: modelPart.rationale },
      warnings,
    };
  }

  async getMatchedFarms(modelId: number): Promise<MatchedFarm[]> {
    const res = await apiService.get<MatchedFarm[]>(`/pest-disease/models/${modelId}/farms`);
    return unwrap(res);
  }

  async getPestsForFarm(farmId: number): Promise<PestModel[]> {
    const res = await apiService.get<PestModel[]>(`/pest-disease/farms/${farmId}/pests`);
    return unwrap(res);
  }

  // ── biofix + activation ─────────────────────────────────────────────────────
  async getBiofix(modelId: number, farmId: number, season: number): Promise<PestBiofix | null> {
    const res = await apiService.get<PestBiofix | null>(
      `/pest-disease/models/${modelId}/biofix?farm_id=${farmId}&season=${season}`
    );
    return res?.success ? res.data : null;
  }

  async setBiofix(
    modelId: number,
    data: { farm_id: number; season_year: number; biofix_date: string; source?: string; note?: string }
  ): Promise<PestBiofix> {
    const res = await apiService.post<PestBiofix>(`/pest-disease/models/${modelId}/biofix`, data);
    return unwrap(res);
  }

  async setActivation(
    modelId: number,
    data: { farm_id: number; enabled: boolean; param_overrides?: Record<string, unknown> | null }
  ): Promise<void> {
    unwrap(await apiService.post(`/pest-disease/models/${modelId}/activation`, data));
  }

  // ── risk series (dashboard reads: null-tolerant) ────────────────────────────
  async getRisk(farmId: number, opts: RiskOpts): Promise<PestRiskSeries | null> {
    const p = new URLSearchParams({ pest_model_id: String(opts.pestModelId) });
    if (opts.season) p.set('season', String(opts.season));
    if (opts.refresh) p.set('refresh', '1');
    const res = await apiService.get<PestRiskSeries>(`/pest-disease/farms/${farmId}/risk?${p.toString()}`);
    return res?.success && res.data ? res.data : null;
  }

  async getOverview(): Promise<PestOverviewItem[]> {
    const res = await apiService.get<{ items: PestOverviewItem[] }>('/pest-disease/overview');
    return res?.success && res.data ? res.data.items : [];
  }

  // ── registered products + target-pest links ────────────────────────────────
  async getChemicals(modelId: number, otherCrops = false): Promise<PestChemicalsResult> {
    const res = await apiService.get<PestChemicalsResult>(
      `/pest-disease/models/${modelId}/chemicals${otherCrops ? '?other_crops=1' : ''}`,
    );
    return unwrap(res);
  }

  async setTargetPests(modelId: number, targetPests: Array<Pick<PestTargetLink, 'label' | 'apvma_pestcode'>>): Promise<PestTargetLink[]> {
    const res = await apiService.put<PestTargetLink[]>(`/pest-disease/models/${modelId}/target-pests`, { target_pests: targetPests });
    return unwrap(res);
  }

  async searchRegisteredPests(q: string): Promise<RegisteredPestOption[]> {
    const res = await apiService.get<RegisteredPestOption[]>(`/pest-disease/registered-pests?q=${encodeURIComponent(q)}`);
    return unwrap(res);
  }

  /** Protection per planting. Throws, so the panel can say why it is blank. */
  async getProtection(farmId: number, pestModelId: number, refresh = false): Promise<FarmProtection> {
    const res = await apiService.get<FarmProtection>(
      `/pest-disease/farms/${farmId}/protection?pest_model_id=${pestModelId}${refresh ? '&refresh=1' : ''}`,
      // A first look may recompute the season's weather.
      { timeout: 60_000 },
    );
    return unwrap(res);
  }

  // ── alert rules ─────────────────────────────────────────────────────────────
  async listRules(pestModelId?: number): Promise<PestAlertRule[]> {
    const q = pestModelId ? `?pest_model_id=${pestModelId}` : '';
    const res = await apiService.get<PestAlertRule[]>(`/pest-disease/alert-rules${q}`);
    return unwrap(res);
  }

  async createRule(data: PestAlertRuleInput): Promise<PestAlertRule> {
    const res = await apiService.post<PestAlertRule>('/pest-disease/alert-rules', data);
    return unwrap(res);
  }

  async updateRule(id: number, data: Partial<PestAlertRuleInput>): Promise<PestAlertRule> {
    const res = await apiService.put<PestAlertRule>(`/pest-disease/alert-rules/${id}`, data);
    return unwrap(res);
  }

  async deleteRule(id: number): Promise<void> {
    unwrap(await apiService.delete(`/pest-disease/alert-rules/${id}`));
  }

  async listEvents(id: number, limit = 50): Promise<PestAlertEvent[]> {
    const res = await apiService.get<PestAlertEvent[]>(`/pest-disease/alert-rules/${id}/events?limit=${limit}`);
    return unwrap(res);
  }

  async testRule(id: number): Promise<{ fired: number; checked: number }> {
    const res = await apiService.post<{ fired: number; checked: number }>(`/pest-disease/alert-rules/${id}/test`, {});
    return unwrap(res);
  }
}

export const pestModelService = new PestModelService();
export default pestModelService;
